import React, { useState, useEffect } from 'react';
import api from '../../../../config/axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import { FaTimes } from 'react-icons/fa';
import Paginacao from '../../../../components/utils/paginacao';

const DetalhesComissoesModal = ({ idColaborador, nomeColaborador, inicio, fim, onClose }) => {
    const [comissoesDetalhadas, setComissoesDetalhadas] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 10;

    useEffect(() => {
        if (idColaborador) {
            fetchComissoesDetalhadas(1);
        }
    }, [idColaborador]);

    const fetchComissoesDetalhadas = async (page = 1) => {
        try {
            Swal.fire({
                title: "Carregando...",
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const formatarDataParaAPI = (data) => {
                if (!data) return '';
                if (data.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    return data;
                }
                if (data.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                    const [dia, mes, ano] = data.split('/');
                    return `${ano}-${mes}-${dia}`;
                }
                return moment(data).format('YYYY-MM-DD');
            };

            const dataInicio = formatarDataParaAPI(inicio);
            const dataFim = formatarDataParaAPI(fim);

            const response = await api.get(`/comissao/historico/${idColaborador}?page=${page}&limit=${limit}&dataInicio=${dataInicio}&dataFim=${dataFim}`);

            if (response.data.success) {
                setComissoesDetalhadas(response.data.data.records || []);
                setTotalPages(response.data.data.totalPaginas || 1);
                setCurrentPage(response.data.data.paginaAtual || 1);
            } else {
                Swal.fire('Erro', 'Erro ao carregar detalhes das comissões.', 'error');
            }
            Swal.close();
        } catch (error) {
            Swal.close();
            Swal.fire('Erro', 'Erro ao carregar detalhes das comissões.', 'error');
        }
    };

    const formatarMoeda = (valor) => {
        if (!valor) return 'R$ 0,00';
        return parseFloat(valor).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    };

    const formatarDataHora = (data) => {
        return moment(data).format('DD/MM/YYYY HH:mm:ss');
    };

    const formatarPercentual = (valor) => {
        if (!valor) return '0%';
        return `${parseFloat(valor).toFixed(1)}%`;
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'criada': { class: 'bg-secondary', text: 'Criada' },
            'cancelada': { class: 'bg-danger', text: 'Cancelada' },
            'paga': { class: 'bg-success', text: 'Paga' }
        };

        const statusInfo = statusMap[status?.toLowerCase()] || { class: 'bg-secondary', text: status || 'N/A' };

        return (
            <span className={`badge ${statusInfo.class}`}>
                {statusInfo.text}
            </span>
        );
    };

    const cancelarComissao = async (comissao) => {
        const result = await Swal.fire({
            title: 'Cancelar Comissão',
            html: `
                <div class="text-start mb-3">
                    <p><strong>Serviço/Produto:</strong> ${comissao.nome}</p>
                    <p><strong>Quantidade Total:</strong> ${comissao.quantidade}</p>
                    <p><strong>Valor da Comissão:</strong> ${formatarMoeda(comissao.valorComissao)}</p>
                </div>
                <div class="text-start">
                    <label for="qtdCancelar" class="form-label">Quantidade a cancelar:</label>
                    <input type="number" id="qtdCancelar" class="form-control" min="1" max="${comissao.quantidade}" value="1" />
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Cancelar Comissão',
            cancelButtonText: 'Voltar',
            preConfirm: () => {
                const qtdCancelar = document.getElementById('qtdCancelar').value;
                if (!qtdCancelar || qtdCancelar <= 0) {
                    Swal.showValidationMessage('Por favor, informe uma quantidade válida');
                    return false;
                }
                if (parseInt(qtdCancelar) > parseInt(comissao.quantidade)) {
                    Swal.showValidationMessage('A quantidade não pode ser maior que a quantidade total');
                    return false;
                }
                return qtdCancelar;
            }
        });

        if (result.isConfirmed && result.value) {
            try {
                Swal.fire({
                    title: "Carregando...",
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const response = await api.put(`/comissao/${comissao.id}/cancelar/${result.value}`);

                if (response.data.success) {
                    Swal.fire('Sucesso!', 'Comissão cancelada com sucesso.', 'success');
                    fetchComissoesDetalhadas(currentPage);
                } else {
                    Swal.fire('Erro', response.data.message || 'Erro ao cancelar a comissão.', 'error');
                }
            } catch (error) {
                Swal.close();
                Swal.fire('Erro', error.response.data.message, 'error');
            }
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchComissoesDetalhadas(page);
    };

    return (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered">
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h4 className="fw-bold mt-2">Detalhes de Comissão</h4>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body">
                        <div className="row mt-3 mb-3">
                            <div className="col-lg-6 col-12">
                                <label className="form-label fw-bold">Colaborador:</label> {nomeColaborador}
                            </div>
                            <div className="col-lg-6 col-12 mt-lg-0 mt-3">
                                <label className="form-label fw-bold">Período:</label> {inicio} - {fim}
                            </div>
                        </div>

                        {comissoesDetalhadas.length > 0 && (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-striped">
                                        <thead className="table-custom-primary">
                                            <tr>
                                                <th>Data e Hora</th>
                                                <th>Comanda</th>
                                                <th>Cliente</th>
                                                <th className="text-center">Tipo</th>
                                                <th>Nome</th>
                                                <th className="text-center">Quantidade</th>
                                                <th className="text-center text-nowrap">Valor Total</th>
                                                <th className="text-center text-nowrap">% Comissão</th>
                                                <th className="text-center text-nowrap">Valor Comissão</th>
                                                <th className="text-center">Status</th>
                                                <th className="text-center">Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {comissoesDetalhadas.map((comissao) => (
                                                <tr key={comissao.id}>
                                                    <td className="text-nowrap">
                                                        {formatarDataHora(comissao.data)}
                                                    </td>
                                                    <td>
                                                        {comissao.comanda}
                                                    </td>
                                                    <td className="text-nowrap">
                                                        {comissao.cliente}
                                                    </td>
                                                    <td className="text-center">
                                                        <span className={`badge ${(comissao.tipo === 'Serviço') ? 'bg-primary' : (comissao.tipo === 'Produto') ? 'bg-info' : 'bg-success'}`}>
                                                            {comissao.tipo}
                                                        </span>
                                                    </td>
                                                    <td className="text-nowrap">
                                                        {comissao.nome}
                                                    </td>
                                                    <td className="text-center">
                                                        {comissao.quantidade}
                                                    </td>
                                                    <td className="text-center text-nowrap">
                                                        {formatarMoeda(comissao.valorBase)}
                                                    </td>
                                                    <td className="text-center">
                                                        {formatarPercentual(comissao.percentualComissao)}
                                                    </td>
                                                    {comissao?.status?.toLowerCase() !== 'cancelada' && (
                                                        <td className="text-center text-nowrap fw-bold text-success">
                                                            {formatarMoeda(comissao.valorComissao)}
                                                        </td>
                                                    )}
                                                    {comissao?.status?.toLowerCase() === 'cancelada' && (
                                                        <td className="text-center text-nowrap fw-bold text-danger">
                                                            {formatarMoeda(comissao.valorComissao)}
                                                        </td>
                                                    )}
                                                    <td className="text-center">
                                                        {getStatusBadge(comissao.status)}
                                                    </td>
                                                    <td className="text-center text-nowrap">
                                                        {comissao.status?.toLowerCase() === 'criada' && comissao.quantidadeCancelada < comissao.quantidade && (
                                                            <button className="btn btn-custom-secondary btn-sm" onClick={() => cancelarComissao(comissao)}>
                                                                <FaTimes />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="d-flex justify-content-end mt-4">
                                    <Paginacao
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button className="btn btn-outline-danger" type="button" onClick={onClose}>
                            <FaTimes /> Fechar
                        </button>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default DetalhesComissoesModal;
